import { api } from '@/shared/api/client'
import { qs, type QueryValue } from '@/shared/lib/query'

/**
 * The turnout feature's server calls, in one place.
 *
 * ## Why this file exists
 *
 * Every page in this module was calling `fetch('/api/v1/turnout/…')` directly. That
 * works in the browser and works in mock mode — `mock-api.ts` replaces `window.fetch`,
 * so the interceptor caught it — and it **cannot work against the real server**, because
 * the bearer token is attached by `shared/api/client`, not by `fetch`. Every page was one
 * 401 away from being empty, and mock mode was hiding it.
 *
 * `api.get` / `api.post` also give us the RFC 9457 problem body as a thrown `ApiError`,
 * which `explain()` renders. The raw calls used `r.ok ? r.json() : null`, which turns
 * every refusal — a 403 on a missing authority, a 409 on a budget cap — into an empty
 * screen with no message. This module's own rule is that the server's refusal is what
 * speaks; a swallowed one breaks it.
 */

// ── shapes ─────────────────────────────────────────────────────────────────
// Hand-written rather than pulled from `openapi/schema.d.ts` because the generated
// types are deeply nested behind `components['schemas']`, which is how every other
// feature here does it too (see `polling/api.ts`).

export interface TurnoutRollup {
  id: string
  electionId: string
  unitId: string
  /** Resolved server-side through UnitLookup; null if the unit was dissolved. */
  unitName: string | null
  unitPath: string | null
  unitType: string
  bucketCode: string
  bucketAt: string
  registeredCount: number
  cellsTotal: number
  cellsActive: number
  cellsWithCoordinator: number
  membersActive: number
  membersContactable: number
  pledgesLive: number
  pledgesCommitted: number
  transportGaps: number
  votedCount: number
  cumulativeVoted: number
  inQueueCount: number
  outstandingCount: number
  escalationsOpen: number
  escalationsResolved: number
  incentiveSpend: string | number | null
  incentiveCurrency: string | null
  incentiveAwards: number
  incentiveRecipients: number
  computedAt: string
}

export interface TurnoutCell {
  id: string
  electionId: string
  stationId: string
  /** Resolved server-side; null if the station was dissolved. */
  stationName: string | null
  /** The cell's ltree scope path — what a write made from this cell must carry. */
  unitPath: string
  code: string
  label: string | null
  statusCode: string
  coordinatorMemberId: string | null
  targetSize: number
  memberCount: number
  formationSourceCode: string
  meetingPoint: string | null
  latitude: number | null
  longitude: number | null
  contactName: string | null
  /** Masked; the number needs turnout:read-raw. */
  contactPhoneMask: string | null
  contactPhoneCountry: string | null
  notes: string | null
  archivedAt: string | null
  archiveReason: string | null
  version: number
  createdAt: string
}

export interface TurnoutCellMember {
  id: string
  cellId: string
  electionId: string
  /** The register id — what consent and every other voter-keyed read is addressed by. */
  voterId: string
  displayName: string
  maskedContact: string | null
  roleCode: string
  joinedAt: string
  leftAt: string | null
  leftReasonCode: string | null
  addedViaCode: string
  isHoldout: boolean
  version: number
}

export interface TurnoutCheckin {
  id: string
  electionId: string
  voterId: string
  /** Masked to initials unless the caller holds turnout:read-raw; null if unresolvable. */
  voterDisplayName: string | null
  cellId: string | null
  stationId: string
  /** Resolved server-side; null if the station was dissolved. */
  stationName: string | null
  stateCode: string
  confidenceCode: string
  clientRecordedAt: string
  serverReceivedAt: string
}

export interface IncentiveBudget {
  id: string
  electionId: string
  code: string
  name: string
  purposeCode: string
  currency: string
  /** ltree scope path; null when the budget is not scoped to a unit. */
  unitPath: string | null
  capAmount: string | number
  committedAmount: string | number
  /** Served by the API — cap minus committed, computed server-side. */
  remainingAmount: string | number
  scopeUnitId: string | null
  programId: string | null
  statusCode: string
  opensOn: string | null
  closesOn: string | null
  approvedAt: string | null
  note: string | null
  createdAt: string
}

export interface TransportOffer {
  id: string
  electionId: string
  cellId: string | null
  stationId: string
  /** Resolved server-side; null if the station was dissolved. */
  stationName: string | null
  offeredByMemberId: string | null
  /** Masked to initials unless the caller holds turnout:read-raw. */
  offeredByDisplayName: string | null
  offerTypeCode: string
  seats: number
  seatsTaken: number
  originLabel: string | null
  windowSlotCode: string | null
  statusCode: string
  createdAt: string
}

export interface TransportRequest {
  id: string
  electionId: string
  cellId: string | null
  stationId: string
  /** Resolved server-side; null if the station was dissolved. */
  stationName: string | null
  memberId: string
  /** Masked to initials unless the caller holds turnout:read-raw. */
  memberDisplayName: string | null
  pickupLabel: string | null
  passengerCount: number
  accessibilityNeeds: string[] | null
  needBasisCode: string
  windowSlotCode: string | null
  statusCode: string
  matchedOfferId: string | null
  matchedAt: string | null
  completedAt: string | null
  createdAt: string
}

// ── elections ──────────────────────────────────────────────────────────────

/*
   Elections moved OUT of turnout into their own feature — they are platform reference
   data that `survey` and `politics` will reference too. Re-exported here so this
   module's screens keep one import, but there is exactly one implementation and one
   route (`/api/v1/elections`).

   `advanceElection` also took a `toStatus` body that the server never read: the phase
   sequence is fixed and the endpoint takes no body at all.
*/
export {
  listElections, getElection, advanceElection, type Election as TurnoutElection,
} from '@/features/elections/api'

// ── rollups ────────────────────────────────────────────────────────────────

export const totalRollup = (electionId: string, unitId: string) =>
  api.get<TurnoutRollup>(
    `/turnout/elections/${electionId}/rollup/total${qs({ unitId } as Record<string, QueryValue>)}`)

export const stationRollups = (electionId: string) =>
  api.get<TurnoutRollup[]>(`/turnout/elections/${electionId}/rollup/stations`)

/**
 * The turnout curve.
 *
 * `from` and `to` are optional — they used to be REQUIRED server-side, so this call, which
 * sent neither, was a 400 (`Required parameter 'from' is missing`) every time. A browser
 * cannot know when polling day was; the election does. Omitting the window now means
 * "every hourly bucket there is", which is what a curve is.
 */
export const hourlyRollup = (
  electionId: string, unitId: string, from?: string, to?: string,
) => api.get<TurnoutRollup[]>(
  `/turnout/elections/${electionId}/rollup/hourly${qs(
    { unitId, from, to } as Record<string, QueryValue>)}`)

// ── cells ──────────────────────────────────────────────────────────────────

export const cellStatuses = () => api.get<string[]>('/turnout/cells/vocabulary/statuses')

export const listCells = (filter: Record<string, QueryValue> = {}) =>
  api.get<TurnoutCell[]>(`/turnout/cells${qs(filter)}`)

/** Only a cell that never had a member; one with history is a 409 turnout/cell-not-empty. */
export const deleteCell = (cellId: string) => api.del<void>(`/turnout/cells/${cellId}`)

export const archiveCell = (cellId: string, reason: string) =>
  api.post<TurnoutCell>(`/turnout/cells/${cellId}/archive${qs({ reason })}`, undefined)

export const getCell = (id: string) =>
  api.get<TurnoutCell>(`/turnout/cells/${id}`)

export const listCellMembers = (cellId: string) =>
  api.get<TurnoutCellMember[]>(`/turnout/cells/${cellId}/members`)

/**
 * Where something was captured, or why the device could not say.
 *
 * One shape across the whole API. The backend requires it on any write from a bound field
 * device and accepts its absence from the web, so a browser may leave it off entirely — but
 * when it IS sent it has to be this, not a loose latitude/longitude pair.
 */
export interface GeoPointInput {
  latitude?: number | null
  longitude?: number | null
  accuracyM?: number | null
  /** Device clock at the moment of the fix, not of the send. */
  capturedAt?: string | null
  /** DENIED | DISABLED | NO_FIX | TIMED_OUT | TOO_INACCURATE. Never with a fix. */
  unavailableReason?: string | null
}

export interface CellCreateInput {
  electionId: string
  stationId: string
  label?: string | null
  targetSize: number
  meetingPoint?: string | null
  notes?: string | null
  /** Optional from the web. Replaces the former latitude/longitude pair. */
  geo?: GeoPointInput | null
  contactName?: string | null
  /** ISO 3166-1 alpha-2. Required whenever contactPhone is set — see PhoneInput. */
  contactPhoneCountry?: string | null
  /** As typed. Normalised to E.164 and sealed on write; only ever read back masked. */
  contactPhone?: string | null
}

/** Create a cell by hand. The assignment engine is an accelerator, not the only way in. */
export const createCell = (body: CellCreateInput) =>
  api.post<TurnoutCell>('/turnout/cells', body)

export interface EnrolInput {
  voterId: string
  householdId?: string | null
  mobilizerId?: string | null
  /** Only true records a CELL_MEMBERSHIP consent; absent records none. */
  consentToMembership?: boolean | null
  consentMethodCode?: string | null
  /** Optional from the web; required from the field app. */
  geo?: GeoPointInput | null
}

/**
 * Add one member. The server refuses a voter who is already in a cell for this election
 * with 409 `turnout/member-already-enrolled` — one active cell per voter is enforced both
 * there and by the partial unique index.
 */
export const enrolMember = (cellId: string, body: EnrolInput) =>
  api.post<TurnoutCellMember>(`/turnout/cells/${cellId}/members`, body)

export const removeMember = (cellId: string, membershipId: string, reason: string) =>
  api.del<void>(`/turnout/cells/${cellId}/members/${membershipId}${qs({ reason })}`)

export const setCoordinator = (cellId: string, membershipId: string) =>
  api.post<TurnoutCell>(`/turnout/cells/${cellId}/coordinator`, { membershipId })

/**
 * Step the coordinator back down to an ordinary member — they stay in the cell.
 *
 * The counterpart `setCoordinator` has always demanded: it refuses a cell that already
 * has one and says "unassign them first", which named nothing any surface offered.
 */
export const unsetCoordinator = (cellId: string) =>
  api.del<TurnoutCell>(`/turnout/cells/${cellId}/coordinator`)

/** The coordinator's real number. `turnout:read-raw`, audited per call server-side. */
export interface CoordinatorContact {
  membershipId: string
  voterId: string
  displayName: string
  phoneE164: string | null
  contactable: boolean
  reasonCode: string | null
}

/**
 * Fetched at the moment of dialling, never held.
 *
 * The board's `maskedContact` is not an answer to "can I ring them" — the server sets it
 * to the literal string CONTACT_AVAILABLE for every member of every board. This endpoint
 * asks the register properly: it checks suppression and consent, and returns the number
 * only when the answer is yes.
 */
export const coordinatorContact = (cellId: string) =>
  api.get<CoordinatorContact>(`/turnout/cells/${cellId}/coordinator/contact`)

// ── consent ────────────────────────────────────────────────────────────────
//
// A cell member's consent is not a one-shot at enrolment. Somebody joins at the gate and
// agrees to be rung two days later; somebody agrees on the spot and changes their mind.
// The table is append-only and a withdrawal is a new row with `granted: false`, so both
// directions are the same call and the history survives either way.

export interface VoterConsent {
  id: string
  voterId: string
  purpose: string
  granted: boolean
  methodCode: string | null
  capturedAt: string
  expiresAt: string | null
}

/** `voter:read`. Newest first per purpose is what decides the current answer. */
export const memberConsents = (voterId: string) =>
  api.get<VoterConsent[]>(`/voters/${voterId}/consents`)

/** `voter:consent`. Granting and withdrawing are the same POST. */
export const recordMemberConsent = (
  voterId: string,
  purposeCode: string,
  granted: boolean,
  methodCode = 'VERBAL_WITNESSED',
) => api.post<VoterConsent>(`/voters/${voterId}/consents`,
  { purposeCode, granted, methodCode })

/** What a coordinator is actually asked to record about a cell member. */
export const CELL_CONSENTS = [
  { code: 'CELL_MEMBERSHIP', label: 'Appear on the coordinator\u2019s board' },
  { code: 'CONTACT_CALL', label: 'Be telephoned' },
  { code: 'CONTACT_SMS', label: 'Be sent an SMS' },
  { code: 'TURNOUT_REMINDERS', label: 'Receive polling-day reminders' },
  { code: 'TRANSPORT_COORDINATION', label: 'Be offered a lift' },
] as const

export const coordinatorCandidates = (cellId: string) =>
  api.get<unknown[]>(`/turnout/cells/${cellId}/coordinator-candidates`)

/**
 * Compute a proposal. **Query parameters, not a body** — the controller declares
 * `@RequestParam electionId/stationId/targetSize/maxDiameterM`, so a JSON body is
 * silently ignored and the call 400s on the missing required parameter.
 */
export const proposeCells = (p: {
  electionId: string; stationId: string; targetSize?: number; maxDiameterM?: number
}) => api.post<CellProposal>(`/turnout/cells/proposals${qs({
  electionId: p.electionId, stationId: p.stationId,
  targetSize: p.targetSize, maxDiameterM: p.maxDiameterM,
} as Record<string, QueryValue>)}`, undefined)

export interface CellProposal {
  id: string
  electionId: string
  stationId: string
  statusCode: string
  targetSize: number
  maxDiameterM: number
  strategyCode: string
  inputVoters: number
  proposedCells: number
  unassignable: number
  /** Raw JSON string of the proposed clusters — the server sends text, not an object. */
  proposal: string | null
  /** Also a string, not an array. Parse before rendering. */
  warnings: string | null
  computedAt: string
  appliedAt: string | null
  discardedAt: string | null
  version: number
}

export const applyProposal = (proposalId: string) =>
  api.post<void>(`/turnout/cells/proposals/${proposalId}/apply`, undefined)

export const discardProposal = (proposalId: string) =>
  api.post<void>(`/turnout/cells/proposals/${proposalId}/discard`, undefined)

// ── pledges ────────────────────────────────────────────────────────────────

export interface Pledge {
  id: string
  electionId: string
  voterId: string
  cellId: string | null
  /** Masked to initials — the full name comes from the audited lookup. */
  voterDisplayName: string | null
  intentCode: string
  plannedSlotCode: string | null
  departureLabel: string | null
  transportModeCode: string | null
  travelMinutes: number | null
  travellingWithCount: number | null
  workConflict: boolean | null
  workConflictNote: string | null
  assistanceNeeds: string[] | null
  assistanceNote: string | null
  completeness: number | null
  hasTransportGap: boolean
  captureChannelCode: string
  capturedAt: string
  supersededAt: string | null
}

export interface PledgeInput {
  electionId: string
  voterId: string
  cellId?: string | null
  intentCode: string
  plannedSlotCode?: string | null
  departureLabel?: string | null
  transportModeCode?: string | null
  travelMinutes?: number | null
  travellingWithCount?: number | null
  workConflict?: boolean | null
  workConflictNote?: string | null
  assistanceNeeds?: string[] | null
  assistanceNote?: string | null
  captureChannelCode: string
  capturedByMobilizerId?: string | null
  /** Only true records a TURNOUT_REMINDERS consent. Absent records none. */
  consentToReminders?: boolean | null
}

/**
 * The pledge list.
 *
 * There is no "pledges at a station" endpoint and never was — the list is by ELECTION,
 * optionally narrowed to one intent. The page used to call `?stationId=…`, which bound to
 * nothing, 400'd, and then tried to `.map` the problem document.
 */
export const listPledges = (electionId: string, intent?: string, page = 0, size = 50) =>
  api.get<Pledge[]>(`/turnout/pledges/by-intent${qs({ electionId, intent, page, size })}`)

/** The ONE live pledge for a voter, or null when they have not made one. */
export const livePledge = (electionId: string, voterId: string) =>
  api.get<Pledge | null>(`/turnout/pledges${qs({ electionId, voterId })}`)

export const pledgeHistory = (electionId: string, voterId: string) =>
  api.get<Pledge[]>(`/turnout/pledges/history${qs({ electionId, voterId })}`)

export const capturePledge = (body: PledgeInput) =>
  api.post<Pledge>('/turnout/pledges', body)

/** Supersedes the live pledge; the old row stays, which is what intent decay reads. */
export const supersedePledge = (body: PledgeInput) =>
  api.put<Pledge>('/turnout/pledges', body)

export interface TurnoutIdentity {
  inRegister: boolean
  sourceCode: string
  voterId: string | null
  displayName: string | null
  genderCode: string | null
  birthYear: number | null
  registrationStatusCode: string | null
  sentence: string
}

/**
 * Who does this national ID belong to? Register first, eCitizen second.
 *
 * POST, not GET — a national ID in a URL lands in access logs, browser history and every
 * proxy in between.
 */
export const identifyByNationalId = (nationalId: string) =>
  api.post<TurnoutIdentity>('/turnout/pledges/identify', { nationalId })

export const pledgeIntents = () => api.get<string[]>('/turnout/pledges/vocabulary/intents')
export const pledgeSlots = () => api.get<string[]>('/turnout/pledges/vocabulary/slots')
export const pledgeTransportModes = () =>
  api.get<string[]>('/turnout/pledges/vocabulary/transport-modes')
export const pledgeCaptureChannels = () =>
  api.get<string[]>('/turnout/pledges/vocabulary/capture-channels')

// ── polling day ────────────────────────────────────────────────────────────

/**
 * The live feed.
 *
 * **Not `GET /turnout/checkins`.** That route answers "what is THIS voter's state" and
 * requires a `voterId`; called with only an `electionId` it 400s, which is what the live
 * screen was doing — and then tried to `.map` the problem document. `/recent` is the
 * election-wide read, added for this screen.
 */
export const listRecentCheckins = (
  electionId: string, filter: { stationId?: string; state?: string; limit?: number } = {},
) => api.get<TurnoutCheckin[]>(
  `/turnout/checkins/recent${qs({ electionId, ...filter } as Record<string, QueryValue>)}`)

/** Live counts per state, computed by the database — not by counting a truncated feed. */
export const checkinCounts = (electionId: string, stationId?: string) =>
  api.get<Record<string, number>>(
    `/turnout/checkins/counts${qs({ electionId, stationId } as Record<string, QueryValue>)}`)

/** The ONE live check-in for a voter, or a 404 when they have none. */
export const liveCheckin = (electionId: string, voterId: string) =>
  api.get<TurnoutCheckin>(`/turnout/checkins${qs({ electionId, voterId })}`)

export const checkinStates = () =>
  api.get<string[]>('/turnout/checkins/vocabulary/states')
export const checkinConfidenceLevels = () =>
  api.get<string[]>('/turnout/checkins/vocabulary/confidence-levels')

export interface CheckinInput {
  electionId: string
  voterId: string
  cellId?: string | null
  stationId: string
  /** ltree path — copied from the cell, which copied it from the station. */
  unitPath: string
  stateCode: string
  reportChannelCode: string
  /** Device clock. The server stores both; velocity curves read this one. */
  clientRecordedAt: string
  /** Idempotency key. A retry with the same one returns the original row, not a duplicate. */
  clientId: string
  deviceId?: string | null
  reportedByMemberId?: string | null
  note?: string | null
}

/**
 * Record a voter's polling-day state — the board's mark-voted control.
 *
 * The server refuses a report that would regress a terminal state (409
 * `turnout/checkin-terminal`) and raises an integrity flag. Show the refusal; it is the
 * rule doing its job, not an error to swallow.
 */
export const recordCheckin = (body: CheckinInput) =>
  api.post<TurnoutCheckin>('/turnout/checkins', body)

// ── the coordinator board ──────────────────────────────────────────────────

export interface BoardMember {
  membershipId: string
  voterId: string
  householdId: string | null
  displayName: string
  maskedContact: string | null
  roleCode: string
  isCoordinator: boolean
  /** Null when this person has no check-in at all — not the same as NOT_STARTED. */
  checkinStateCode: string | null
  checkinConfidenceCode: string | null
  checkinAt: string | null
  pledgeIntentCode: string | null
  plannedSlotCode: string | null
  hasTransportGap: boolean
}

export interface Board {
  cellId: string
  electionId: string
  stationId: string
  stationName: string | null
  /** The cell's ltree scope path — what a check-in written from this board carries. */
  unitPath: string
  cellCode: string
  cellLabel: string | null
  statusCode: string
  meetingPoint: string | null
  members: number
  voted: number
  inQueue: number
  enRoute: number
  outstanding: number
  transportGaps: number
  rows: BoardMember[]
}

/**
 * Members, live check-in state and pledge for one cell, in ONE call.
 *
 * Composed server-side on purpose: this screen is used on a phone, in the field, and
 * three lists joined in the browser is three round trips over that connection. Rows come
 * back already in working order — outstanding first, grouped by household.
 */
export const cellBoard = (cellId: string) =>
  api.get<Board>(`/turnout/cells/${cellId}/board`)

// ── queue reports ──────────────────────────────────────────────────────────

export interface QueueReport {
  id: string
  electionId: string
  stationId: string
  /** Resolved server-side; null if the station was dissolved. */
  stationName: string | null
  unitPath: string | null
  queueBandCode: string
  estimatedWaitMin: number | null
  estimatedPeople: number | null
  stationOpen: boolean
  /** Raw JSON array of StationIssue codes — the server sends text. Parse before rendering. */
  issues: string | null
  note: string | null
  photoFileId: string | null
  reportedAt: string
  createdAt: string
}

/** Current queue at every station in the election, worst first. */
export const currentQueueReports = (electionId: string) =>
  api.get<QueueReport[]>(`/turnout/queue-reports/current${qs({ electionId })}`)

export const queueBands = () =>
  api.get<string[]>('/turnout/queue-reports/vocabulary/queue-bands')
export const stationIssues = () =>
  api.get<string[]>('/turnout/queue-reports/vocabulary/station-issues')

export interface QueueReportInput {
  electionId: string
  stationId: string
  unitPath: string
  queueBandCode: string
  estimatedWaitMin?: number | null
  estimatedPeople?: number | null
  stationOpen?: boolean | null
  issueCodes?: string[] | null
  note?: string | null
}

export const submitQueueReport = (body: QueueReportInput) =>
  api.post<QueueReport>('/turnout/queue-reports', body)

// ── transport ──────────────────────────────────────────────────────────────

/*
   `stationId` used to be REQUIRED on both of these server-side, so these two calls —
   which send only an election — were a 400 every time. It is optional now; pass a station
   to narrow, omit it for the whole election.
*/

export const listTransportOffers = (electionId: string, stationId?: string) =>
  api.get<TransportOffer[]>(
    `/turnout/transport/offers${qs({ electionId, stationId } as Record<string, QueryValue>)}`)

export const listTransportRequests = (electionId: string, stationId?: string) =>
  api.get<TransportRequest[]>(
    `/turnout/transport/requests${qs({ electionId, stationId } as Record<string, QueryValue>)}`)

/** Offers that can still take a passenger at one station — the matcher's narrower view. */
export const availableTransportOffers = (electionId: string, stationId: string) =>
  api.get<TransportOffer[]>(`/turnout/transport/offers${qs({
    electionId, stationId, available: true } as Record<string, QueryValue>)}`)

export const transportOfferTypes = () =>
  api.get<string[]>('/turnout/transport/vocabulary/offer-types')
export const transportNeedBases = () =>
  api.get<string[]>('/turnout/transport/vocabulary/need-bases')
export const transportOfferStatuses = () =>
  api.get<string[]>('/turnout/transport/vocabulary/offer-statuses')
export const transportRequestStatuses = () =>
  api.get<string[]>('/turnout/transport/vocabulary/request-statuses')

export interface TransportOfferInput {
  electionId: string
  cellId?: string | null
  stationId: string
  unitPath: string
  offeredByMemberId?: string | null
  offerTypeCode: string
  seats: number
  originLabel?: string | null
  windowSlotCode?: string | null
}

export const createTransportOffer = (body: TransportOfferInput) =>
  api.post<TransportOffer>('/turnout/transport/offers', body)

/** PUT, not POST — withdrawing changes the state of an existing offer. */
export const withdrawTransportOffer = (offerId: string) =>
  api.put<TransportOffer>(`/turnout/transport/offers/${offerId}/withdraw`, undefined)

export interface TransportRequestInput {
  electionId: string
  cellId?: string | null
  stationId: string
  unitPath: string
  memberId: string
  pickupLabel?: string | null
  passengerCount: number
  accessibilityNeedCodes?: string[] | null
  /** NOT NULL and no OTHER — the audit trail depends on a real reason. */
  needBasisCode: string
  distanceM?: number | null
  windowSlotCode?: string | null
}

export const createTransportRequest = (body: TransportRequestInput) =>
  api.post<TransportRequest>('/turnout/transport/requests', body)

/**
 * Fill a request from an offer. Body, not a query param — see AssignCoordinatorRequest.
 *
 * Claims a seat atomically. The server refuses with 409 when the offer filled in between,
 * which is exactly what happens when two coordinators match the same vehicle at once.
 */
export const matchTransportRequest = (requestId: string, offerId: string) =>
  api.post<TransportRequest>(`/turnout/transport/requests/${requestId}/match`, { offerId })

// ── incentives ─────────────────────────────────────────────────────────────

export const listIncentiveBudgets = (electionId: string) =>
  api.get<IncentiveBudget[]>(`/turnout/elections/${electionId}/incentive-budgets`)

export interface IncentiveAward {
  id: string
  electionId: string
  budgetId: string
  entryTypeCode: string
  recipientKindCode: string
  cellId: string | null
  /** The cell's label or code — resolved server-side. */
  cellName: string | null
  memberId: string | null
  /** Masked to initials unless the caller holds turnout:read-raw. */
  memberDisplayName: string | null
  stationId: string | null
  stationName: string | null
  incentiveKindCode: string
  awardReasonCode: string
  quantity: string | number
  unitValue: string | number
  amount: string | number
  currency: string
  valueBasisCode: string
  deliveryChannelCode: string
  deliveredOn: string
  externalReference: string | null
  reversesId: string | null
  reversalReasonCode: string | null
  note: string | null
  createdAt: string
}

/** Awards in a budget, optionally narrowed to one cell and/or one member. */
export const listAwards = (
  budgetId: string, filter: { cellId?: string; memberId?: string } = {},
) => api.get<IncentiveAward[]>(
  `/turnout/incentive-budgets/${budgetId}/awards${qs(filter as Record<string, QueryValue>)}`)

export interface IncentiveBudgetInput {
  electionId: string
  code: string
  name: string
  purposeCode: string
  currency: string
  capAmount: number
  scopeUnitId?: string | null
  /** Optional on the server — a budget with no unit scope is a national one. */
  unitPath?: string | null
  programId?: string | null
  opensOn?: string | null
  closesOn?: string | null
  note?: string | null
}

/** A new budget starts DRAFT. No award can be recorded until it is approved. */
export const createIncentiveBudget = (electionId: string, body: IncentiveBudgetInput) =>
  api.post<IncentiveBudget>(`/turnout/elections/${electionId}/incentive-budgets`, body)

/** DRAFT → ACTIVE. The server refuses a budget that is not DRAFT with a 409. */
export const approveBudget = (budgetId: string) =>
  api.post<IncentiveBudget>(`/turnout/incentive-budgets/${budgetId}/approve`, {})

export interface IncentiveAwardInput {
  electionId: string
  budgetId: string
  recipientKindCode: string
  cellId?: string | null
  memberId?: string | null
  stationId?: string | null
  unitPath: string
  incentiveKindCode: string
  awardReasonCode: string
  quantity: number
  unitValue: number
  currency: string
  valueBasisCode: string
  deliveryChannelCode: string
  deliveredOn: string
  externalReference?: string | null
  note?: string | null
}

/**
 * Record an award against an ACTIVE budget.
 *
 * The cap is a server rule: an award that would take committed past the cap is refused
 * with 409 `turnout/budget-cap-exceeded`. The control is offered and the refusal speaks —
 * disabling the button client-side would hide which rule stopped it.
 */
export const recordAward = (budgetId: string, body: IncentiveAwardInput) =>
  api.post<IncentiveAward>(`/turnout/incentive-budgets/${budgetId}/awards`, body)

/**
 * Reverse an award — writes a NEW row with a negative amount. Never a delete.
 *
 * The body is `{reversesId, reversalReasonCode, note}`, which is what
 * `AwardReversalRequest` declares. This used to send `{reason}`: the required fields were
 * absent, so every reversal 400'd on validation before it reached the service.
 */
export const reverseAward = (
  awardId: string, reversalReasonCode: string, note?: string | null,
) => api.post<IncentiveAward>(`/turnout/awards/${awardId}/reverse`,
  { reversesId: awardId, reversalReasonCode, note: note ?? null })

export const budgetPurposes = () =>
  api.get<string[]>('/turnout/incentives/vocabulary/purposes')
export const budgetStatuses = () =>
  api.get<string[]>('/turnout/incentives/vocabulary/budget-statuses')
export const incentiveKinds = () =>
  api.get<string[]>('/turnout/incentives/vocabulary/incentive-kinds')
export const awardReasons = () =>
  api.get<string[]>('/turnout/incentives/vocabulary/award-reasons')
export const valueBases = () =>
  api.get<string[]>('/turnout/incentives/vocabulary/value-bases')
export const deliveryChannels = () =>
  api.get<string[]>('/turnout/incentives/vocabulary/delivery-channels')
export const reversalReasons = () =>
  api.get<string[]>('/turnout/incentives/vocabulary/reversal-reasons')
export const recipientKinds = () =>
  api.get<string[]>('/turnout/incentives/vocabulary/recipient-kinds')

// ── integrity ──────────────────────────────────────────────────────────────

/*
   New in this pass. The flags themselves are not: `CheckinService` has raised a
   CHECKIN_TERMINAL_REGRESSION row every time a confirmed vote was contradicted, since the
   feature shipped. The table, the entity and the `turnout:moderate` authority all existed;
   there was no endpoint, so the rows accumulated where nothing could read them.
*/

export interface IntegrityFlag {
  id: string
  electionId: string
  subjectTypeCode: string
  subjectId: string
  /** What the flag is ABOUT, in words — a voter's or member's name. Null when not a person. */
  subjectLabel: string | null
  unitPath: string | null
  /** The unit named by unitPath; null when the path names nothing. */
  unitName: string | null
  ruleCode: string
  severityCode: string
  /** The rule's own JSON context. Shape varies by ruleCode — render it, do not assume it. */
  detail: string | null
  statusCode: string
  detectedAt: string
  reviewedBy: string | null
  reviewedAt: string | null
  resolutionCode: string | null
  resolutionNote: string | null
  createdAt: string
}

/** No status filter returns everything, resolved rows included — the audit trail. */
export const listIntegrityFlags = (
  electionId: string, filter: { status?: string; severity?: string; limit?: number } = {},
) => api.get<IntegrityFlag[]>(
  `/turnout/integrity/flags${qs({ electionId, ...filter } as Record<string, QueryValue>)}`)

export const integrityCounts = (electionId: string) =>
  api.get<Record<string, number>>(`/turnout/integrity/counts${qs({ electionId })}`)

/** Says who is looking, so two operators do not work the same anomaly. */
export const claimFlag = (flagId: string) =>
  api.post<IntegrityFlag>(`/turnout/integrity/flags/${flagId}/claim`, {})

/** Closes the flag with a resolution. The row stays — nothing here deletes a flag. */
export const resolveFlag = (flagId: string, resolutionCode: string, note?: string | null) =>
  api.post<IntegrityFlag>(`/turnout/integrity/flags/${flagId}/resolve`,
    { resolutionCode, note: note ?? null })

export const flagStatuses = () =>
  api.get<string[]>('/turnout/integrity/vocabulary/statuses')
export const flagSeverities = () =>
  api.get<string[]>('/turnout/integrity/vocabulary/severities')
export const flagResolutions = () =>
  api.get<string[]>('/turnout/integrity/vocabulary/resolutions')
export const flagSubjects = () =>
  api.get<string[]>('/turnout/integrity/vocabulary/subjects')

// ── the floor ────────────────────────────────────────────────────────────────

/**
 * The Floor console's payload — `GET /turnout/elections/{id}/floor`.
 *
 * ## The whole day arrives in one request, and that is the design
 *
 * Every cell ships its cumulative count for every hour, so scrubbing and replaying the day are
 * pure client-side animation over data already in hand. Fetching per hour would put a network
 * round trip inside a drag gesture, which is the difference between a scrubber and a slideshow
 * — and on a board somebody is dragging through during polling, a stutter reads as the data
 * being wrong rather than the request being slow.
 */
export interface FloorDay {
  electionId: string
  electionName: string
  opensAt: string
  closesAt: string
  now: string
  /** The moment the board is drawn at. The scrubber moves this; it is not always `now`. */
  asAt: string
  /** The newest check-in. A board showing `now` with a stale report draws a gap as calm. */
  lastReportAt: string | null
  minutesToClose: number
  /** How much of the polling window has gone, 0..1 — the pace every cell is judged against. */
  elapsedShare: number
  pollsOpen: boolean
}

export interface FloorTotals {
  cells: number
  activeCells: number
  members: number
  pledged: number
  voted: number
  inQueue: number
  enRoute: number
  outstanding: number
  transportGaps: number
  escalationsOpen: number
  escalationsLate: number
  cellsBehind: number
  cellsStalled: number
}

export interface FloorBeat {
  at: string
  /** The hour's own count — the velocity. A cumulative curve alone cannot show a stall. */
  votedInHour: number
  cumulativeVoted: number
  inQueue: number
  requiredPace: number
}

export interface FloorCell {
  id: string
  code: string
  label: string | null
  stationId: string
  stationName: string | null
  statusCode: string
  coordinatorName: string | null
  meetingPoint: string | null
  members: number
  pledged: number
  voted: number
  inQueue: number
  enRoute: number
  outstanding: number
  transportGaps: number
  escalationsOpen: number
  share: number
  paceShare: number
  /** `paceShare − share`. Positive is behind; the sign is the point. */
  behind: number
  /** Members an hour it must still move. Null once the polls have shut. */
  perHourNeeded: number | null
  /** Minutes since this cell's last report. Null when it has never reported. */
  stalledMinutes: number | null
  /** Cumulative voted per hour, aligned to `hours`. */
  series: number[]
}

export interface FloorStation {
  id: string
  name: string | null
  cells: number
  members: number
  voted: number
  outstanding: number
  share: number
}

export interface FloorEscalation {
  id: string
  cellId: string | null
  cellCode: string | null
  stationName: string | null
  tierCode: string
  triggerRule: string
  statusCode: string
  triggeredAt: string
  dueAt: string | null
  /** Positive once past due. Null when it has no due time — a different state from on time. */
  minutesLate: number | null
}

export interface Floor {
  day: FloorDay
  hours: string[]
  totals: FloorTotals | null
  series: FloorBeat[]
  cells: FloorCell[]
  stations: FloorStation[]
  escalations: FloorEscalation[]
  unavailable: string | null
}

export const getFloor = (electionId: string, p: { unitId?: string, at?: string } = {}) =>
  api.get<Floor>(`/turnout/elections/${electionId}/floor${qs({ unitId: p.unitId, at: p.at })}`)

// ── pace checkpoints ───────────────────────────────────────────────────────
//
// "Which stations are behind, and which of their cells" — taken at 11:00 and 13:00 and
// kept. A checkpoint is computed once, so these numbers do not move when the page is
// refreshed: what the 11:00 report said is what it said.

export interface PaceCell {
  id: string
  cellId: string
  cellCode: string
  cellLabel: string | null
  membersActive: number
  votedCount: number
  votedShare: number
  shortfallPp: number
  behind: boolean
  /** Who can actually do something about it. There is no write endpoint behind this. */
  coordinatorName: string | null
  mobilizerId: string | null
  mobilizerName: string | null
  contactMask: string | null
}

export interface PaceStation {
  id: string
  stationId: string
  stationName: string
  unitPath: string
  membersActive: number
  votedCount: number
  votedShare: number
  shortfallPp: number
  band: 'BEHIND' | 'FAR_BEHIND' | string
  cellsTotal: number
  cellsBehind: number
  cells: PaceCell[]
}

export interface PaceReport {
  id: string
  electionId: string
  electionCode: string | null
  electionName: string | null
  checkpointCode: string
  checkpointAt: string
  pollsOpenAt: string
  pollsCloseAt: string
  windowElapsedPct: number
  membersActive: number
  votedCount: number
  /** The bar every station is held to: the whole campaign at the same minute. */
  fieldVotedShare: number
  stationsTotal: number
  stationsBehind: number
  cellsBehind: number
  behindRatio: number
  minGapPp: number
  minMembers: number
  notifiedAt: string | null
  notifyError: string | null
  computedAt: string
  stations: PaceStation[]
}

export const listPaceChecks = (electionId: string) =>
  api.get<PaceReport[]>(`/turnout/elections/${electionId}/pace`)

export const getPaceReport = (checkId: string) =>
  api.get<PaceReport>(`/turnout/pace/${checkId}`)

/** Take (or re-take) a checkpoint now. Idempotent on the label and the polling day. */
export const takePaceCheck = (electionId: string, p: { code?: string, at?: string } = {}) =>
  api.post<PaceReport>(`/turnout/elections/${electionId}/pace${qs({ code: p.code, at: p.at })}`, {})
